/**
 * organization.ts
 * Shared Zod contracts for identity.organization — global, cross-tenant
 * client identity (add-kernel-organization-identity, design.md §1).
 *
 * Distinct from Client: `organization` is who a real-world company IS,
 * shared by every tenant that deals with it. `client` (clients.ts) is the
 * tenant-scoped COMMERCIAL RELATIONSHIP with that organization.
 *
 * NOT tenant-scoped — no companyId on the wire, unlike every other kernel
 * entity in this package.
 */
import { z } from 'zod';
import type { PaginatedResponse } from './pagination.js';

/**
 * Closed vocabulary, purely descriptive (UI filters only) — never a source of
 * authorization. Enforced at the DB via chk_organization_kind (migration 0082).
 */
export const OrganizationKindSchema = z.enum(['operator', 'contractor', 'both', 'supplier']);
export type OrganizationKind = z.infer<typeof OrganizationKindSchema>;

export const OrganizationSchema = z.object({
  id: z.string().uuid(),
  legalName: z.string(),
  cuit: z.string().nullable(),
  kind: OrganizationKindSchema,
  taxCondition: z.string().nullable(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  parentOrgId: z.string().uuid().nullable(),
  linkedCompanyId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});

export type Organization = z.infer<typeof OrganizationSchema>;

/** OrganizationListResponse — reuses the kernel PaginatedResponse shape. */
export type OrganizationListResponse = PaginatedResponse<Organization>;

// ---------------------------------------------------------------------------
// Create / update bodies — Slice 4 (design.md §4/§5).
// No companyId anywhere — organization has none (this table is the whole
// point of NOT being tenant-scoped).
// ---------------------------------------------------------------------------

export const CreateOrganizationBodySchema = z.object({
  legalName: z.string().min(1, 'Legal name is required.').max(256),
  cuit: z.string().max(32).optional(),
  kind: OrganizationKindSchema.optional().default('operator'),
  taxCondition: z.string().max(128).optional(),
  address: z.string().max(512).optional(),
  phone: z.string().max(64).optional(),
  email: z.string().email().max(256).optional(),
  parentOrgId: z.string().uuid().optional(),
});

export type CreateOrganizationBody = z.infer<typeof CreateOrganizationBodySchema>;

export const UpdateOrganizationBodySchema = z.object({
  legalName: z.string().min(1, 'Legal name is required.').max(256).optional(),
  cuit: z.string().max(32).optional(),
  kind: OrganizationKindSchema.optional(),
  taxCondition: z.string().max(128).optional(),
  address: z.string().max(512).optional(),
  phone: z.string().max(64).optional(),
  email: z.string().email().max(256).optional(),
  parentOrgId: z.string().uuid().nullable().optional(),
});

export type UpdateOrganizationBody = z.infer<typeof UpdateOrganizationBodySchema>;

/**
 * CUIT normalized to digits-only (strips dashes/dots/spaces) — the canonical
 * dedupe key and the form organization.cuit is stored in (design.md §1,
 * backfill script comment). Shared between the kernel routes/use-cases and
 * the backend backfill script so both sides of the split agree on one
 * definition — display formatting is a separate, frontend-only concern.
 */
export function normalizeCuit(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\D/g, '');
}

// ---------------------------------------------------------------------------
// CUIT módulo-11 checksum validator (KC-9.2, add-cuentas-pagar).
// Next to normalizeCuit() so CUIT validation has exactly one home
// (design.md D6). Purely additive: normalizeCuit() itself is unchanged, and
// no existing KC-1..KC-8 requirement's observable behavior changes.
//
// NOTE (apply-progress risk, PR1/Phase 0): `CuitSchema` below is NOT
// re-exported from `./index.ts` — `packages/contracts-kernel/src/index.ts`
// already re-exports an UNRELATED `CuitSchema` from `./platform-company.ts`
// (CP-2: loose `NN-NNNNNNNN-N` format check, no checksum, optional field).
// Re-exporting both under the same name from the barrel is a TS duplicate
// export error. `validateCuit` (the symbol KC-9.2 actually requires the
// package to export) IS re-exported — see index.ts. Whoever implements
// Phase 1 (SUMI CUIT hygiene, task 1.4, `sumi.routes.ts` alta uses
// `CuitSchema`) MUST resolve this naming collision first — e.g. rename one
// of the two schemas — before importing this `CuitSchema` through the
// package barrel. Flagged, not silently resolved (design.md did not
// anticipate the existing CP-2 export when it named this schema).
// ---------------------------------------------------------------------------

/**
 * Módulo 11 check digit + exact 11-digit length over ALREADY-NORMALIZED
 * digits. Name is literal per KC-9.2: `validateCuit`, not `isValidCuit`.
 * Pure, synchronous, import-free — same discipline as `matchCapability`
 * (kernel-authorization AZ-2.2).
 */
export function validateCuit(digits: string): boolean {
  if (!/^\d{11}$/.test(digits)) return false;

  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += Number(digits.charAt(i)) * weights[i]!;
  }
  const mod = sum % 11;
  let verifier = 11 - mod;
  if (verifier === 11) verifier = 0;
  if (verifier === 10) return false; // no representable single check digit

  return verifier === Number(digits.charAt(10));
}

/** Normalises first (normalizeCuit), then validates. Use at every alta boundary. */
export const CuitSchema = z
  .string()
  .transform(normalizeCuit)
  .refine(validateCuit, {
    message: 'CUIT inválido (11 dígitos + dígito verificador).',
  });

// ---------------------------------------------------------------------------
// Platform-tier company grouping — add-cost-center-group-sharing follow-up
// ---------------------------------------------------------------------------
// Distinct from the tenant-scoped /identity/organizations CRUD above: a
// platform admin (separate auth scope, no tenant JWT) links a company to the
// "empresa matriz" of its group so catalog.tenant_group_company_ids() (RLS,
// migration 0086) has data to resolve. See platform-companies.routes.ts'
// GET/PUT /platform/companies/:id/organization.

export const CompanyOrganizationResponseSchema = z.object({
  parentCompanyId: z.string().uuid().nullable(),
  parentCompanyName: z.string().nullable(),
});

export type CompanyOrganizationResponse = z.infer<typeof CompanyOrganizationResponseSchema>;

export const SetCompanyOrganizationBodySchema = z.object({
  parentCompanyId: z.string().uuid().nullable(),
});

export type SetCompanyOrganizationBody = z.infer<typeof SetCompanyOrganizationBodySchema>;

// ---------------------------------------------------------------------------
// "Razón Social" picker — merge-holding-group-tenant, design.md §1/§2
// ---------------------------------------------------------------------------
// GET /identity/organizations/for-tenant-group — scoped to the CURRENT
// tenant's holding group only (same group platform admins wire via the
// "empresa matriz" UI above). Empty for single-firma tenants — the frontend
// hides the picker entirely rather than showing a dropdown with nothing to
// pick.

export const GroupLegalEntitySchema = z.object({
  id: z.string().uuid(),
  legalName: z.string(),
});

export type GroupLegalEntity = z.infer<typeof GroupLegalEntitySchema>;

export const GroupLegalEntityListResponseSchema = z.object({
  items: z.array(GroupLegalEntitySchema),
});

export type GroupLegalEntityListResponse = z.infer<typeof GroupLegalEntityListResponseSchema>;

// ---------------------------------------------------------------------------
// identity.tenant_partnership — bilateral, revocable cross-tenant consent
// (cross-tenant-kernel-mechanism, design.md CT1 / CT5). NOT tenant-scoped on
// the wire in the usual sense: both `ownerCompanyId` and `counterpartyCompanyId`
// are first-class participants, same category as Organization above.
// ---------------------------------------------------------------------------

/**
 * Closed, code-owned vocabulary. A partnership grant is named per-mechanism
 * — never global (enforced at the DB via chk_tenant_partnership_scope,
 * widened by migration 0190 to admit 'locx.service_order' alongside the
 * original 'locx.work_permit', kernel-cross-tenant-service-write XT1/XT9).
 */
export const TenantPartnershipScopeSchema = z.enum(['locx.work_permit', 'locx.service_order']);
export type TenantPartnershipScope = z.infer<typeof TenantPartnershipScopeSchema>;

/**
 * Closed vocabulary. Rows are never deleted — revocation is a status change,
 * and re-partnering after a revoke inserts a NEW row (design.md CT1).
 */
export const TenantPartnershipStatusSchema = z.enum(['pending', 'active', 'revoked']);
export type TenantPartnershipStatus = z.infer<typeof TenantPartnershipStatusSchema>;

export const TenantPartnershipSchema = z.object({
  id: z.string().uuid(),
  ownerCompanyId: z.string().uuid(),
  counterpartyCompanyId: z.string().uuid(),
  scope: TenantPartnershipScopeSchema,
  status: TenantPartnershipStatusSchema,
  requestedByCompanyId: z.string().uuid(),
  requestedByUserId: z.string().uuid(),
  requestedAt: z.string().datetime(),
  acceptedByUserId: z.string().uuid().nullable(),
  acceptedAt: z.string().datetime().nullable(),
  revokedByCompanyId: z.string().uuid().nullable(),
  revokedByUserId: z.string().uuid().nullable(),
  revokedAt: z.string().datetime().nullable(),
});

export type TenantPartnership = z.infer<typeof TenantPartnershipSchema>;
